import { isPlatformBrowser as isPlatformBrowser$1, CommonModule } from '@angular/common';
import { Injectable, NgZone, defineInjectable, inject, Directive, Inject, PLATFORM_ID, ElementRef, Input, Output, EventEmitter, NgModule } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { __spread, __values } from 'tslib';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/*******************************************************************************
 * @license
 * Copyright (c) 2020 Piotr Stępniewski <k3nsei.pl@gmail.com>
 * (https://www.linkedin.com/in/piotrstepniewski/)
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://opensource.org/licenses/MIT
 */
/** @enum {string} */
var InViewportConfigDirection = {
    BOTH: 'both',
    VERTICAL: 'vertical',
    HORIZONTAL: 'horizontal',
};
/**
 * @return {?}
 */
function isPlatformBrowser() {
    try {
        return typeof window !== 'undefined' && this === window;
    }
    catch (e) {
        return false;
    }
}
/**
 * @param {?} input
 * @return {?}
 */
function toBase64(input) {
    return !isPlatformBrowser()
        ? toBase64Node(input)
        : toBase64Browser(input);
}
/**
 * @param {?} input
 * @return {?}
 */
function toBase64Node(input) {
    try {
        return typeof global !== 'undefined' && global.Buffer.from(input).toString('base64');
    }
    catch (e) {
        return input;
    }
}
/**
 * @param {?} input
 * @return {?}
 */
function toBase64Browser(input) {
    try {
        return typeof window !== 'undefined' && window.btoa(input);
    }
    catch (e) {
        return input;
    }
}
var InViewportConfig = /** @class */ (function () {
    function InViewportConfig(options) {
        var _this = this;
        this._rootMargin = '0px 0px 0px 0px';
        this._threshold = __spread(InViewportConfig.DEFAULT_THRESHOLD);
        this._partial = true;
        this._direction = InViewportConfigDirection.BOTH;
        if (Object.prototype.toString.call(options) === '[object Object]') {
            ['root', 'rootMargin', 'threshold', 'partial', 'direction', 'checkFn'].forEach(function (prop) {
                if (options.hasOwnProperty(prop)) {
                    _this[prop] = options[prop];
                }
            });
        }
        this._hash = InViewportConfig.hash({
            rootMargin: this.rootMargin,
            threshold: this.threshold,
            partial: this.partial,
            direction: this.direction,
            checkFn: String(this.checkFn)
        });
    }
    /**
     * @private
     * @param {?} input
     * @return {?}
     */
    InViewportConfig.stringify = /**
     * @private
     * @param {?} input
     * @return {?}
     */
    function (input) {
        var e_1, _a;
        if (Array.isArray(input)) {
            /** @type {?} */
            var stringifiedArr = [];
            // tslint:disable-next-line:prefer-for-of
            for (var i = 0; i < input.length; i++) {
                stringifiedArr.push(InViewportConfig.stringify(input[i]));
            }
            return "[" + stringifiedArr.join(',') + "]";
        }
        else if (typeof input === 'object' && input !== null) {
            /** @type {?} */
            var acc = [];
            /** @type {?} */
            var sortedKeys = Object.keys(input).sort();
            try {
                for (var sortedKeys_1 = __values(sortedKeys), sortedKeys_1_1 = sortedKeys_1.next(); !sortedKeys_1_1.done; sortedKeys_1_1 = sortedKeys_1.next()) {
                    var k = sortedKeys_1_1.value;
                    /** @type {?} */
                    var v = InViewportConfig.stringify(input[k]);
                    acc.push(k + ":" + v);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (sortedKeys_1_1 && !sortedKeys_1_1.done && (_a = sortedKeys_1.return)) _a.call(sortedKeys_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
            return acc.join(InViewportConfig.STRINGIFY_DELIMITER);
        }
        return String(input);
    };
    /**
     * @private
     * @param {?} input
     * @return {?}
     */
    InViewportConfig.hash = /**
     * @private
     * @param {?} input
     * @return {?}
     */
    function (input) {
        return toBase64(InViewportConfig.stringify(input));
    };
    Object.defineProperty(InViewportConfig.prototype, "root", {
        get: /**
         * @return {?}
         */
        function () {
            return this._root;
        },
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            this._root = value && value.nodeType === 1 ? value : undefined;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InViewportConfig.prototype, "rootMargin", {
        get: /**
         * @return {?}
         */
        function () {
            return this._rootMargin;
        },
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            if (!value || typeof value !== 'string') {
                this._rootMargin = '0px 0px 0px 0px';
            }
            else {
                /** @type {?} */
                var marginString = value || '0px';
                /** @type {?} */
                var margins = marginString.split(new RegExp('\\s+')).map(function (margin) {
                    /** @type {?} */
                    var parts = /^(-?\d*\.?\d+)(px|%)$/.exec(margin);
                    if (!parts) {
                        throw new TypeError('rootMargin must be specified in pixels or percent');
                    }
                    return "" + parts[1] + parts[2];
                });
                margins[1] = margins[1] || margins[0];
                margins[2] = margins[2] || margins[0];
                margins[3] = margins[3] || margins[1];
                this._rootMargin = margins.join(' ');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InViewportConfig.prototype, "threshold", {
        get: /**
         * @return {?}
         */
        function () {
            return this._threshold;
        },
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            /** @type {?} */
            var threshold = [];
            /** @type {?} */
            var isValidThreshold = function (val) { return typeof val === 'number' && val >= 0 && val <= 1; };
            if (isValidThreshold(value)) {
                threshold = [value];
            }
            else if (Array.isArray(value) && value.length) {
                threshold = value.filter(function (val) { return isValidThreshold(val); });
            }
            if (threshold.length === 0) {
                threshold = __spread(InViewportConfig.DEFAULT_THRESHOLD);
            }
            this._threshold = threshold;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InViewportConfig.prototype, "partial", {
        get: /**
         * @return {?}
         */
        function () {
            return this._partial;
        },
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            this._partial = !!value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InViewportConfig.prototype, "direction", {
        get: /**
         * @return {?}
         */
        function () {
            return this._direction;
        },
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            /** @type {?} */
            var isValidValue = function (val) {
                return ([
                    InViewportConfigDirection.BOTH,
                    InViewportConfigDirection.HORIZONTAL,
                    InViewportConfigDirection.VERTICAL
                ].indexOf(val) >= 0);
            };
            this._direction = isValidValue(value) ? value : InViewportConfigDirection.BOTH;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InViewportConfig.prototype, "hash", {
        get: /**
         * @return {?}
         */
        function () {
            return this._hash;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InViewportConfig.prototype, "checkFn", {
        get: /**
         * @return {?}
         */
        function () {
            return this._checkFn;
        },
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            this._checkFn = value;
        },
        enumerable: true,
        configurable: true
    });
    InViewportConfig.DEFAULT_THRESHOLD = [0, 1];
    InViewportConfig.STRINGIFY_DELIMITER = '|';
    return InViewportConfig;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var InViewportService = /** @class */ (function () {
    function InViewportService(ngZone) {
        this.ngZone = ngZone;
        this.trigger$ = new Subject();
        this.registry = [];
    }
    /**
     * @private
     * @param {?} entries
     * @return {?}
     */
    InViewportService.prototype.emitTrigger = /**
     * @private
     * @param {?} entries
     * @return {?}
     */
    function (entries) {
        var _this = this;
        if (Array.isArray(entries) && entries.length) {
            entries.forEach(function (entry) { return _this.trigger$.next(entry); });
        }
    };
    /**
     * @private
     * @param {?=} element
     * @return {?}
     */
    InViewportService.prototype.getRootElement = /**
     * @private
     * @param {?=} element
     * @return {?}
     */
    function (element) {
        return element && element.nodeType === Node.ELEMENT_NODE ? element : null;
    };
    /**
     * @private
     * @param {?} root
     * @param {?} configHash
     * @return {?}
     */
    InViewportService.prototype.findEntry = /**
     * @private
     * @param {?} root
     * @param {?} configHash
     * @return {?}
     */
    function (root, configHash) {
        var _this = this;
        return this.registry.find(function (entry) { return entry.root === _this.getRootElement(root) && entry.configHash === configHash; });
    };
    /**
     * @param {?} target
     * @param {?} config
     * @return {?}
     */
    InViewportService.prototype.register = /**
     * @param {?} target
     * @param {?} config
     * @return {?}
     */
    function (target, config) {
        var _this = this;
        this.ngZone.runOutsideAngular(function () {
            /** @type {?} */
            var foundedEntry = _this.findEntry(config.root, config.hash);
            if (foundedEntry && !foundedEntry.targets.has(target)) {
                foundedEntry.targets.add(target);
                foundedEntry.observer.observe(target);
            }
            else {
                /** @type {?} */
                var root = _this.getRootElement(config.root);
                /** @type {?} */
                var options = {
                    root: root !== null ? root : undefined,
                    rootMargin: config.rootMargin,
                    threshold: config.threshold
                };
                /** @type {?} */
                var entry = {
                    root: root,
                    configHash: config.hash,
                    targets: new Set([target]),
                    observer: new IntersectionObserver(function (entries) { return _this.ngZone.run(function () { return _this.emitTrigger(entries); }); }, options)
                };
                entry.observer.observe(target);
                _this.registry = __spread(_this.registry, [entry]);
            }
        });
    };
    /**
     * @param {?} target
     * @param {?} config
     * @return {?}
     */
    InViewportService.prototype.unregister = /**
     * @param {?} target
     * @param {?} config
     * @return {?}
     */
    function (target, config) {
        var _this = this;
        this.ngZone.runOutsideAngular(function () {
            /** @type {?} */
            var foundedEntry = _this.findEntry(config.root, config.hash);
            if (foundedEntry) {
                var observer = foundedEntry.observer, targets = foundedEntry.targets;
                if (targets.has(target)) {
                    observer.unobserve(target);
                    targets.delete(target);
                }
                if (targets.size === 0) {
                    observer.disconnect();
                    _this.registry = _this.registry.filter(function (entry) { return entry !== foundedEntry; });
                }
            }
        });
    };
    InViewportService.decorators = [
        { type: Injectable, args: [{
                    providedIn: 'root'
                },] }
    ];
    InViewportService.ctorParameters = function () { return [
        { type: NgZone }
    ]; };
    /** @nocollapse */ InViewportService.ngInjectableDef = defineInjectable({ factory: function InViewportService_Factory() { return new InViewportService(inject(NgZone)); }, token: InViewportService, providedIn: "root" });
    return InViewportService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
var InViewportMetadata = Symbol('InViewportMetadata');
var InViewportDirective = /** @class */ (function () {
    function InViewportDirective(platformId, // tslint:disable-line
    elementRef, inViewport) {
        this.platformId = platformId;
        this.elementRef = elementRef;
        this.inViewport = inViewport;
        this.config = new InViewportConfig();
        this.destroyed$ = new Subject();
        this.inViewportAction = new EventEmitter();
    }
    Object.defineProperty(InViewportDirective.prototype, "options", {
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            this.config = new InViewportConfig(value);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    InViewportDirective.prototype.ngAfterViewInit = /**
     * @return {?}
     */
    function () {
        var _this = this;
        if (isPlatformBrowser$1(this.platformId)) {
            this.inViewport.register(this.elementRef.nativeElement, this.config);
            this.inViewport.trigger$
                .pipe(filter(function (entry) {
                return entry && entry.target === _this.elementRef.nativeElement;
            }), takeUntil(this.destroyed$))
                .subscribe(function (entry) {
                _this.emitAction(entry, false);
            });
        }
        else {
            this.emitAction(undefined, true);
        }
    };
    /**
     * @return {?}
     */
    InViewportDirective.prototype.ngOnDestroy = /**
     * @return {?}
     */
    function () {
        this.destroyed$.next();
        this.destroyed$.complete();
        if (isPlatformBrowser$1(this.platformId)) {
            this.inViewport.unregister(this.elementRef.nativeElement, this.config);
        }
    };
    /**
     * @private
     * @param {?} entry
     * @param {?} force
     * @return {?}
     */
    InViewportDirective.prototype.check = /**
     * @private
     * @param {?} entry
     * @param {?} force
     * @return {?}
     */
    function (entry, force) {
        var _this = this;
        var _a;
        /** @type {?} */
        var isVisible = function () {
            /** @type {?} */
            var partiallyVisible = entry.isIntersecting || entry.intersectionRatio > 0;
            /** @type {?} */
            var completelyVisible = entry.intersectionRatio >= 1;
            return _this.config.partial ? partiallyVisible : completelyVisible;
        };
        /** @type {?} */
        var visible = force || !entry || isVisible();
        return _a = {}, _a[InViewportMetadata] = { entry: entry }, _a.target = this.elementRef.nativeElement, _a.visible = visible, _a;
    };
    /**
     * @private
     * @param {?} entry
     * @param {?} force
     * @return {?}
     */
    InViewportDirective.prototype.emitAction = /**
     * @private
     * @param {?} entry
     * @param {?} force
     * @return {?}
     */
    function (entry, force) {
        /** @type {?} */
        var event = this.config.checkFn
            ? this.config.checkFn(entry, { force: force, config: this.config })
            : this.check(entry, force);
        this.inViewportAction.emit(event);
    };
    InViewportDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[inViewport]'
                },] }
    ];
    InViewportDirective.ctorParameters = function () { return [
        { type: Object, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] },
        { type: ElementRef },
        { type: InViewportService }
    ]; };
    InViewportDirective.propDecorators = {
        options: [{ type: Input, args: ['inViewportOptions',] }],
        inViewportAction: [{ type: Output }]
    };
    return InViewportDirective;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var InViewportModule = /** @class */ (function () {
    function InViewportModule() {
    }
    InViewportModule.decorators = [
        { type: NgModule, args: [{
                    imports: [CommonModule],
                    declarations: [InViewportDirective],
                    exports: [InViewportDirective]
                },] }
    ];
    return InViewportModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { InViewportConfig, InViewportConfigDirection, InViewportDirective, InViewportMetadata, InViewportModule, InViewportService, isPlatformBrowser, toBase64, toBase64Browser, toBase64Node };
//# sourceMappingURL=ng-in-viewport.js.map
